/**
 * Published comparisons between English-proficiency tests, pivoted on IELTS Academic.
 *
 * Every figure below comes from the body that issued it; nothing is interpolated.
 * Retrieved 2026-10-16.
 *
 * 1. IELTS vs TOEFL iBT (tests taken before January 2026), IELTS vs PTE Academic (point
 *    equivalents) and IELTS vs Cambridge English Scale:
 *    https://ielts.org/organisations/ielts-for-organisations/compare-ielts
 *    - TOEFL: "Score concordance study between IELTS Academic and TOEFL iBT". IELTS states
 *      the comparison "applies only to TOEFL iBT tests taken before January 2026" and that
 *      scores from tests taken from January 2026 "should not be assumed to be directly
 *      comparable".
 *    - PTE: "Investigating the relationship between PTE Academic and IELTS Academic"; IELTS
 *      recommends comparing the four skill columns, not only the overall column.
 *    - Cambridge: "Comparing scores to IELTS B2 First and C1 Advanced 2023" (Cambridge
 *      English). Only IELTS 5.0 to 7.5 are listed.
 * 2. Pearson, PTE Academic vs IELTS Academic, minimum PTE thresholds per IELTS band
 *    (concordance updated after research published July 2025):
 *    https://www.pearsonpte.com/scoring/score-comparison
 * 3. ETS, TOEFL iBT 1-6 band scale (reports from 21 January 2026) with its concordance to
 *    the 0-120 total scale (tests taken before 21 January 2026): total-score column of the
 *    table at https://www.ets.org/toefl/test-takers/ibt/scores/understand-scores.html
 * 4. Duolingo, "A Concordance Study between the DET and IELTS Academic SWRL Subscores",
 *    Duolingo Research Report DRR-24-05, 24 October 2025, Table 7 (overall score; Duolingo
 *    kept the 2023 overall mapping): https://go.duolingo.com/concordance-det-ielts-2024
 *    (redirects to the PDF at duolingo-papers.s3.us-east-1.amazonaws.com).
 *
 * Duolingo's DET to TOEFL table is published only as an image on its scores page, which
 * could not be retrieved as text, so there is no DET-TOEFL mapping here.
 */

export type EnglishTest =
  | "ielts"
  | "toefl-old"
  | "toefl-new"
  | "pte"
  | "cambridge"
  | "duolingo";

export const ENGLISH_TESTS: readonly EnglishTest[] = [
  "ielts",
  "toefl-old",
  "toefl-new",
  "pte",
  "cambridge",
  "duolingo",
];

export interface IeltsRow {
  /** IELTS Academic overall band. */
  band: number;
  /** TOEFL iBT total (0-120), tests before January 2026 — IELTS comparison. */
  toefl: readonly [number, number];
  /** PTE Academic overall "point equivalent" from the IELTS comparison. */
  pteEquivalent: number | null;
  /** Lowest PTE Academic overall that Pearson lists for this IELTS band. */
  pteMinimum: number | null;
  /** Cambridge English Scale score from the IELTS comparison (5.0-7.5 only). */
  cambridge: number | null;
  /** Duolingo English Test overall scores that Duolingo concords to this band. */
  duolingo: readonly number[];
}

/** Highest IELTS band first. */
export const IELTS_ROWS: readonly IeltsRow[] = [
  {
    band: 9,
    toefl: [120, 120],
    pteEquivalent: null,
    pteMinimum: 90,
    cambridge: null,
    duolingo: [],
  },
  {
    band: 8.5,
    toefl: [115, 119],
    pteEquivalent: 88.1,
    pteMinimum: 86,
    cambridge: null,
    duolingo: [160],
  },
  {
    band: 8,
    toefl: [108, 114],
    pteEquivalent: 82.3,
    pteMinimum: 79,
    cambridge: null,
    duolingo: [150, 155],
  },
  {
    band: 7.5,
    toefl: [100, 107],
    pteEquivalent: 74.6,
    pteMinimum: 71,
    cambridge: 191,
    duolingo: [140, 145],
  },
  {
    band: 7,
    toefl: [91, 99],
    pteEquivalent: 66.3,
    pteMinimum: 63,
    cambridge: 185,
    duolingo: [130, 135],
  },
  {
    band: 6.5,
    toefl: [81, 90],
    pteEquivalent: 58.5,
    pteMinimum: 55,
    cambridge: 176,
    duolingo: [120, 125],
  },
  {
    band: 6,
    toefl: [67, 80],
    pteEquivalent: 51.6,
    pteMinimum: 47,
    cambridge: 169,
    duolingo: [110, 115],
  },
  {
    band: 5.5,
    toefl: [51, 66],
    pteEquivalent: 45.4,
    pteMinimum: 39,
    cambridge: 162,
    duolingo: [95, 100, 105],
  },
  {
    band: 5,
    toefl: [37, 50],
    pteEquivalent: 40.8,
    pteMinimum: 31,
    cambridge: 154,
    duolingo: [85, 90],
  },
  {
    band: 4.5,
    toefl: [26, 36],
    pteEquivalent: null,
    pteMinimum: 24,
    cambridge: null,
    duolingo: [70, 75, 80],
  },
  {
    band: 4,
    toefl: [14, 25],
    pteEquivalent: null,
    pteMinimum: null,
    cambridge: null,
    duolingo: [65],
  },
];

export interface ToeflNewBand {
  band: number;
  /** Lowest 0-120 total in the band (ETS concordance). */
  minTotal: number;
  /** Highest 0-120 total in the band. */
  maxTotal: number;
}

/** ETS: total-score column of the 1-6 concordance. Highest band first. */
export const TOEFL_NEW_BANDS: readonly ToeflNewBand[] = [
  { band: 6, minTotal: 114, maxTotal: 120 },
  { band: 5.5, minTotal: 107, maxTotal: 113 },
  { band: 5, minTotal: 95, maxTotal: 106 },
  { band: 4.5, minTotal: 86, maxTotal: 94 },
  { band: 4, minTotal: 72, maxTotal: 85 },
  { band: 3.5, minTotal: 58, maxTotal: 71 },
  { band: 3, minTotal: 44, maxTotal: 57 },
  { band: 2.5, minTotal: 34, maxTotal: 43 },
  { band: 2, minTotal: 24, maxTotal: 33 },
  { band: 1.5, minTotal: 12, maxTotal: 23 },
  { band: 1, minTotal: 0, maxTotal: 11 },
];

export const PTE_MIN = 10;
export const PTE_MAX = 90;
export const CAMBRIDGE_MIN = 80;
export const CAMBRIDGE_MAX = 230;
export const DET_MIN = 10;
export const DET_MAX = 160;

/** Whether `value` is a legal score for the test. */
export function isValidScore(
  test: EnglishTest,
  value: number | null
): value is number {
  if (value === null || !Number.isFinite(value)) {
    return false;
  }
  switch (test) {
    case "ielts":
      return value >= 0 && value <= 9 && Number.isInteger(value * 2);
    case "toefl-old":
      return Number.isInteger(value) && value >= 0 && value <= 120;
    case "toefl-new":
      return value >= 1 && value <= 6 && Number.isInteger(value * 2);
    case "pte":
      return Number.isInteger(value) && value >= PTE_MIN && value <= PTE_MAX;
    case "cambridge":
      return (
        Number.isInteger(value) &&
        value >= CAMBRIDGE_MIN &&
        value <= CAMBRIDGE_MAX
      );
    case "duolingo":
      return (
        Number.isInteger(value) &&
        value >= DET_MIN &&
        value <= DET_MAX &&
        value % 5 === 0
      );
    default:
      return false;
  }
}

function rowFor(band: number): IeltsRow | undefined {
  return IELTS_ROWS.find((row) => row.band === band);
}

export function toeflNewTotals(band: number): ToeflNewBand | undefined {
  return TOEFL_NEW_BANDS.find((entry) => entry.band === band);
}

/** The ETS 1-6 band that a 0-120 total falls in. */
export function toeflNewBandForTotal(total: number): number | null {
  const entry = TOEFL_NEW_BANDS.find(
    (each) => total >= each.minTotal && total <= each.maxTotal
  );
  return entry ? entry.band : null;
}

export interface Match {
  /** IELTS band(s) matched, lowest first (one band unless a 1-6 TOEFL band spans two). */
  bands: number[];
  /** True when the input is a threshold met ("at least"), not a band's whole range. */
  atLeast: boolean;
}

function matchTotals(min: number, max: number): number[] {
  return IELTS_ROWS.filter((row) => row.toefl[0] <= max && row.toefl[1] >= min)
    .map((row) => row.band)
    .sort((a, b) => a - b);
}

function thresholdMatch(
  pick: (row: IeltsRow) => number | null,
  value: number
): Match | null {
  let best: number | null = null;
  for (const row of IELTS_ROWS) {
    const threshold = pick(row);
    if (
      threshold !== null &&
      value >= threshold &&
      (best === null || row.band > best)
    ) {
      best = row.band;
    }
  }
  return best === null ? null : { bands: [best], atLeast: true };
}

/** The IELTS Academic band(s) that a score on `test` corresponds to, or null if below the published range. */
export function matchIelts(test: EnglishTest, value: number): Match | null {
  switch (test) {
    case "ielts":
      return rowFor(value) ? { bands: [value], atLeast: false } : null;
    case "toefl-old": {
      const bands = matchTotals(value, value);
      return bands.length > 0 ? { bands, atLeast: false } : null;
    }
    case "toefl-new": {
      const entry = toeflNewTotals(value);
      const bands = entry ? matchTotals(entry.minTotal, entry.maxTotal) : [];
      return bands.length > 0 ? { bands, atLeast: false } : null;
    }
    case "pte":
      return thresholdMatch((row) => row.pteMinimum, value);
    case "cambridge":
      return thresholdMatch((row) => row.cambridge, value);
    case "duolingo": {
      const row = IELTS_ROWS.find((each) => each.duolingo.includes(value));
      return row ? { bands: [row.band], atLeast: false } : null;
    }
    default:
      return null;
  }
}

export interface Range {
  min: number;
  max: number;
}

export interface Equivalents {
  ielts: Range;
  toeflOld: Range;
  /** ETS 1-6 bands covering the 0-120 totals, via ETS's own concordance. */
  toeflNew: Range;
  pteEquivalent: Range | null;
  pteMinimum: Range | null;
  cambridge: Range | null;
  duolingo: Range | null;
}

function span(values: readonly (number | null)[]): Range | null {
  const present = values.filter((value): value is number => value !== null);
  return present.length === 0
    ? null
    : { min: Math.min(...present), max: Math.max(...present) };
}

/** The other tests' figures for a set of matched IELTS bands. */
export function equivalentsFor(bands: readonly number[]): Equivalents | null {
  const rows = bands
    .map(rowFor)
    .filter((row): row is IeltsRow => row !== undefined);
  if (rows.length === 0) {
    return null;
  }
  const toeflOld = span(rows.flatMap((row) => [row.toefl[0], row.toefl[1]]));
  if (toeflOld === null) {
    return null;
  }
  const low = toeflNewBandForTotal(toeflOld.min);
  const high = toeflNewBandForTotal(toeflOld.max);
  return {
    ielts: { min: Math.min(...bands), max: Math.max(...bands) },
    toeflOld,
    toeflNew: { min: low ?? 1, max: high ?? 6 },
    pteEquivalent: span(rows.map((row) => row.pteEquivalent)),
    pteMinimum: span(rows.map((row) => row.pteMinimum)),
    cambridge: span(rows.map((row) => row.cambridge)),
    duolingo: span(rows.flatMap((row) => row.duolingo)),
  };
}

export interface Conversion {
  match: Match;
  equivalents: Equivalents;
}

/**
 * Convert a score into the other tests' figures. For TOEFL input the TOEFL rows use ETS's
 * own concordance between the 0-120 totals and the 1-6 bands rather than a detour through
 * the IELTS band.
 */
export function convertScore(
  test: EnglishTest,
  value: number
): Conversion | null {
  const match = matchIelts(test, value);
  const equivalents = match ? equivalentsFor(match.bands) : null;
  if (!(match && equivalents)) {
    return null;
  }
  if (test === "toefl-old") {
    const band = toeflNewBandForTotal(value);
    if (band !== null) {
      equivalents.toeflNew = { min: band, max: band };
    }
  }
  if (test === "toefl-new") {
    const entry = toeflNewTotals(value);
    if (entry) {
      equivalents.toeflOld = { min: entry.minTotal, max: entry.maxTotal };
    }
  }
  return { match, equivalents };
}
